import { useState, useRef, useEffect } from "react";
import "./CreateNewListModal.scss";
import "../../../components/ModalContent/ModalComponents.scss";
import { colorOptions, normalizeColor } from "../../../utils/colors";
import ColorPicker from "../../ColorPicker/ColorPicker";
import { createTodoList } from "../../../api/todoLists";
import useAuthStore from "../../../store/authStore";
import { useTranslation } from "react-i18next";

function CreateNewListModal({ onClose, onListCreated }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [showTitleError, setShowTitleError] = useState(false);
  const [listType, setListType] = useState("todo"); // "todo" or "checklist"
  const [isCreating, setIsCreating] = useState(false);

  const currentProject = useAuthStore((state) => state.currentProject);
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleCreateList = async () => {
    if (!title.trim()) {
      setShowTitleError(true);
      return;
    }

    setIsCreating(true);
    try {
      const colorHex = normalizeColor(selectedColor);
      const newList = await createTodoList({
        title: title.trim(),
        color: colorHex,
        type: listType,
        scope: "project",
        projectId: currentProject?._id,
      });

      if (onListCreated) {
        onListCreated(newList);
      }
    } catch (error) {
      console.error("Error creating list:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setTitle("");
    setSelectedColor(colorOptions[0]);
    setListType("todo");
    setShowTitleError(false);
    onClose();
  };

  return (
    <div className="create-new-list-modal">
      <div className="modal-form">
        <div className="modal-form-title">{t("common.title")}</div>
        <input
          ref={inputRef}
          type="text"
          className="modal-text-input"
          placeholder={t("tasks.links.listTitlePlaceholder")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (showTitleError && e.target.value.trim()) {
              setShowTitleError(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleCreateList();
            }
          }}
        />
        {showTitleError && (
          <div className="modal-helper-text">
            {t("tasks.links.titleRequired")}
          </div>
        )}

        <div className="modal-form-title">{t("tasks.links.type")}</div>
        <div className="modal-checkbox-group">
          <label className="modal-checkbox-label">
            <input
              type="checkbox"
              checked={listType === "todo"}
              onChange={() => setListType("todo")}
            />
            <span>{t("tasks.links.todoList")}</span>
          </label>
          <label className="modal-checkbox-label">
            <input
              type="checkbox"
              checked={listType === "checklist"}
              onChange={() => setListType("checklist")}
            />
            <span>{t("tasks.links.checklist")}</span>
          </label>
        </div>

        <div className="modal-form-title">{t("common.color")}</div>
        <div className="color-picker-wrapper">
          <ColorPicker
            selectedColor={selectedColor}
            onColorSelect={setSelectedColor}
          />
        </div>

        <div className="modal-form-buttons">
          <button
            className="modal-button cancel"
            onClick={handleCancel}
            disabled={isCreating}
          >
            {t("common.cancel")}
          </button>
          <button
            className="modal-button save"
            onClick={handleCreateList}
            disabled={isCreating}
          >
            {isCreating ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateNewListModal;
